import {
  Popover,
  TextInput,
  Group,
  Stack,
  Button,
  ComboboxClearButton,
} from "@mantine/core"
import { DateTime } from "../../../../@client/System"
import { useTranslation } from "react-i18next"
import { useDisclosure } from "@mantine/hooks"
import dayjs from "dayjs"
import {
  DatePicker,
  DatesRangeValue,
  DateValue,
  TimeInput,
} from "@mantine/dates"

export interface DateTimeRange {
  start: DateTime | null
  end: DateTime | null
}

interface Props {
  value: DateTimeRange
  onConfirm: (value: DateTimeRange) => void
  maxDate?: Date
  placeholder?: string
  variant?: "default" | "unstyled"
}

export function DateTimeRangeSelector({
  value,
  onConfirm,
  maxDate,
  placeholder,
  variant,
}: Props) {
  const { t } = useTranslation("app_server")
  const [range, setRange] = React.useState<DatesRangeValue | undefined>([
    value.start ? new Date(value.start) : null,
    value.end ? new Date(value.end) : null,
  ])
  const now = new Date()
  const [opened, { close, toggle }] = useDisclosure()
  const renderRange = useRenderRange()
  return (
    <Popover opened={opened} onClose={close}>
      <Popover.Target>
        <TextInput
          variant={variant}
          placeholder={placeholder}
          onClick={toggle}
          readOnly
          value={renderRange(range?.[0], range?.[1])}
        />
      </Popover.Target>
      <Popover.Dropdown>
        <Stack justify="center">
          <Group justify="space-between">
            <Group justify="flex-start">
              <Button
                variant="default"
                onClick={() => {
                  setRange([
                    new Date(now.setHours(0, 0)),
                    new Date(now.setHours(23, 59)),
                  ])
                }}
              >
                {t("Today")}
              </Button>
              <Button
                variant="default"
                onClick={() => {
                  setRange([dayjs(now).add(-1, "hours").toDate(), now])
                }}
              >
                - 1 Hour
              </Button>
              <Button
                variant="default"
                onClick={() => {
                  setRange([dayjs(now).add(-5, "minutes").toDate(), now])
                }}
              >
                - 5 Minutes
              </Button>
            </Group>
            <ComboboxClearButton onClear={() => setRange([null, null])} />
          </Group>
          <Stack gap="xs">
            <DatePicker
              styles={{ levelsGroup: { justifyContent: "center" } }}
              type="range"
              value={range}
              onChange={(range) => setRange(range)}
              maxDate={maxDate}
            />
            <TimeInput
              label={t("From")}
              value={range?.[0] ? dayjs(range[0]).format("HH:mm") : ""}
              onChange={(event) => {
                const [hours, minutes] = event.target.value
                  .split(":")
                  .map(Number)
                if (range) {
                  const newDate = dayjs(range[0])
                    .hour(hours)
                    .minute(minutes)
                    .toDate()
                  setRange([newDate, range[1]])
                }
              }}
            />
            <TimeInput
              label={t("To")}
              value={range?.[1] ? dayjs(range[1]).format("HH:mm") : ""}
              onChange={(event) => {
                const [hours, minutes] = event.target.value
                  .split(":")
                  .map(Number)
                if (range?.[1]) {
                  const newDate = dayjs(range[1])
                    .hour(hours)
                    .minute(minutes)
                    .toDate()
                  setRange([range[0], newDate])
                } else if (range?.[0]) {
                  const newDate = dayjs(range[0])
                    .hour(hours)
                    .minute(minutes)
                    .toDate()
                  setRange([range[0], newDate])
                }
              }}
            />
          </Stack>
          <Button
            onClick={() => {
              onConfirm({
                start:
                  range && range[0]
                    ? (dayjs(new Date(range[0]))
                        .second(0)
                        .millisecond(0)
                        .utc(true)
                        .toISOString() as DateTime)
                    : null,
                end:
                  range && range[1]
                    ? (dayjs(new Date(range[1]))
                        .second(59)
                        .millisecond(999)
                        .utc(true)
                        .toISOString() as DateTime)
                    : null,
              })
              close()
            }}
          >
            {t("Confirm")}
          </Button>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  )
}

function useRenderRange() {
  const { t } = useTranslation("app_server")
  return (
    start: DateValue | undefined,
    end: DateValue | undefined,
    dateFormat = "L"
  ) => {
    const startDate = start ? dayjs(start).format(dateFormat) : ""
    const endDate = end ? dayjs(end).format(dateFormat) : ""
    const startTime = start ? dayjs(start).format("LT") : ""
    const endTime = end ? dayjs(end).format("LT") : ""
    let result = ""
    if (startDate === endDate) {
      result += startDate
      if (startTime && endTime) {
        result += ` ${startTime}-${endTime}`
      } else if (startTime) {
        result += ` ${t("From_LowerCase")}\u00A0${startTime}`
      } else if (endTime) {
        result += ` ${t("To_LowerCase")}\u00A0${endTime}`
      }
    } else {
      result += startDate
      if (startTime) {
        result += `\u00A0${startTime}`
      }
      result += " -\u00A0"
      result += endDate
      if (endTime) {
        result += `\u00A0${endTime}`
      }
    }
    return result
  }
}
